export type TimestampResolution = {
  timestampMs?: number;
  error?: 'timezone_required' | 'invalid_timezone' | 'invalid_timestamp' | 'ambiguous_local_time' | 'nonexistent_local_time';
};

type WallParts = { year: number; month: number; day: number; hour: number; minute: number; second: number; millisecond: number };

const wallTimePattern = /^(?:(\d{4})-(\d{1,2})-(\d{1,2})|(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4}))(?:[ T](\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?)?$/;
const explicitOffsetPattern = /(?:Z|[+-]\d{2}:?\d{2})$/i;

function parseExplicitOffsetTimestamp(text: string): number | null {
  if (!explicitOffsetPattern.test(text)) return null;
  // If offset has no colon (e.g. 2026-07-06T09:30:00-0500), insert colon for Date.parse compatibility across engines
  const normalized = text.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  const timestampMs = Date.parse(normalized);
  return Number.isFinite(timestampMs) && timestampMs > 0 ? timestampMs : null;
}

function parseWallParts(input: string): WallParts | null {
  const match = input.match(wallTimePattern);
  if (!match) {
    // Keep compatibility with the app's prior human-readable export format
    // (for example "Tue, Feb 03, 2026 - 09:30 AM"). UTC is used only as a
    // neutral extraction frame; the resulting wall fields are still resolved
    // in the caller's selected or automatically resolved IANA source timezone below.
    if (!/[A-Za-z]{3,}/.test(input)) return null;
    const parsed = Date.parse(`${input.replace(/\s+-\s+/, ' ')} UTC`);
    if (!Number.isFinite(parsed)) return null;
    const date = new Date(parsed);
    return {
      year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate(),
      hour: date.getUTCHours(), minute: date.getUTCMinutes(), second: date.getUTCSeconds(),
      millisecond: date.getUTCMilliseconds(),
    };
  }
  const rawYear = match[1] ?? match[6];
  const shortYear = Number(rawYear);
  const year = rawYear.length === 2 ? (shortYear <= 68 ? 2000 + shortYear : 1900 + shortYear) : shortYear;
  const month = Number(match[2] ?? match[4]);
  const day = Number(match[3] ?? match[5]);
  const hour = Number(match[7] ?? 0);
  const minute = Number(match[8] ?? 0);
  const second = Number(match[9] ?? 0);
  const fraction = String(match[10] ?? '').slice(0, 3).padEnd(3, '0');
  const millisecond = fraction ? Number(fraction) : 0;
  const check = new Date(Date.UTC(year, month - 1, day, hour, minute, second, millisecond));
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day
      || hour > 23 || minute > 59 || second > 59) return null;
  return { year, month, day, hour, minute, second, millisecond };
}

function sameWallParts(left: WallParts, right: WallParts): boolean {
  return left.year === right.year && left.month === right.month && left.day === right.day
    && left.hour === right.hour && left.minute === right.minute && left.second === right.second
    && left.millisecond === right.millisecond;
}

function getWallParts(timestampMs: number, timeZone: string): WallParts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(timestampMs));
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    year: Number(values.year), month: Number(values.month), day: Number(values.day),
    hour: Number(values.hour), minute: Number(values.minute), second: Number(values.second),
    millisecond: new Date(timestampMs).getUTCMilliseconds(),
  };
}

function resolveWallTime(parts: WallParts, timeZone: string): TimestampResolution {
  try { new Intl.DateTimeFormat('en-US', { timeZone }).format(0); }
  catch { return { error: 'invalid_timezone' }; }
  const wallAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, parts.millisecond);
  const offsets = new Set<number>();
  // Sample both sides of the wall clock across three days. Six-hour steps
  // capture timezone offsets around DST transitions without scanning every
  // half-hour for every row in a large CSV.
  for (let delta = -36 * 60; delta <= 36 * 60; delta += 6 * 60) {
    const instant = wallAsUtc + delta * 60_000;
    const local = getWallParts(instant, timeZone);
    const localAsUtc = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second, local.millisecond);
    offsets.add(localAsUtc - instant);
  }
  const matches = [...offsets]
    .map((offset) => wallAsUtc - offset)
    .filter((instant) => sameWallParts(getWallParts(instant, timeZone), parts));
  const unique = [...new Set(matches)];
  if (unique.length === 0) return { error: 'nonexistent_local_time' };
  if (unique.length > 1) return { error: 'ambiguous_local_time' };
  return { timestampMs: unique[0] };
}

export function resolveSourceTimestamp(value: unknown, sourceTimeZone?: string): TimestampResolution {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const timestampMs = value < 1e11 ? value * 1000 : value;
    return timestampMs > 0 ? { timestampMs } : { error: 'invalid_timestamp' };
  }
  const text = String(value ?? '').trim();
  if (!text) return { error: 'invalid_timestamp' };
  const numeric = Number(text);
  if (Number.isFinite(numeric)) {
    const timestampMs = numeric < 1e11 ? numeric * 1000 : numeric;
    return timestampMs > 0 ? { timestampMs } : { error: 'invalid_timestamp' };
  }
  const explicitMs = parseExplicitOffsetTimestamp(text);
  if (explicitMs != null) {
    return { timestampMs: explicitMs };
  }
  const zoneSuffix = text.match(/^(.*?)(?:\s+\[([^\]]+)\]|\s+([A-Za-z_]+\/[A-Za-z0-9_+\-/]+)|\s+(UTC|GMT))$/i);
  const wallText = (zoneSuffix?.[1] ?? text).trim();
  const zone = String(zoneSuffix?.[2] ?? zoneSuffix?.[3] ?? zoneSuffix?.[4] ?? sourceTimeZone ?? '').trim();
  const parts = parseWallParts(wallText);
  if (!parts) return { error: 'invalid_timestamp' };
  if (!zone) return { error: 'timezone_required' };
  return resolveWallTime(parts, zone);
}

export function hasNaiveSourceTimestamp(rows: unknown[], fields?: string[]): boolean {
  const aliases = new Set((fields ?? []).map((field) => field.toLowerCase().replace(/[^a-z0-9]/g, '')));
  const timestampNames = new Set(['date', 'datetime', 'time', 'timestamp', 'executedat', 'filledat', 'entrytime', 'entrydate', 'entrydatetime', 'opentime', 'exittime', 'exitdate', 'exitdatetime', 'closetime']);
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || typeof row !== 'object') continue;
    for (const [key, value] of Object.entries(row as Record<string, unknown>)) {
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (aliases.size && !aliases.has(normalizedKey)) continue;
      if (!timestampNames.has(normalizedKey) || value == null || String(value).trim() === '') continue;
      const resolved = resolveSourceTimestamp(value);
      if (resolved.error === 'timezone_required') return true;
    }
  }
  return false;
}

export function getBrowserTimeZone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (zone) { new Intl.DateTimeFormat('en-US', { timeZone: zone }); return zone; }
  } catch {}
  return 'UTC';
}

const csvTimezoneHeaders = new Set([
  'timezone', 'timezoneid', 'timezonename', 'sourcetimezone',
  'exporttimezone', 'brokertimezone', 'tz',
]);

function isValidIanaTimezone(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value.trim() });
    return true;
  } catch {
    return false;
  }
}

/** Prefer an explicit IANA timezone column in CSV metadata, then the caller's
 * configured zone, and finally the browser's validated timezone. */
export function resolveCsvSourceTimezone(
  rows: unknown[],
  fields?: string[],
  preferredTimezone?: string,
): string {
  const timezoneKeys = new Set([
    ...(fields ?? []),
    ...((Array.isArray(rows) ? rows : []).flatMap((row) => row && typeof row === 'object' ? Object.keys(row) : [])),
  ].map((field) => field.toLowerCase().replace(/[^a-z0-9]/g, '')).filter((field) => csvTimezoneHeaders.has(field)));
  const explicitTimezone = (Array.isArray(rows) ? rows : []).flatMap((row) =>
    row && typeof row === 'object'
      ? Object.entries(row).filter(([key]) => timezoneKeys.has(key.toLowerCase().replace(/[^a-z0-9]/g, ''))).map(([, value]) => value)
      : [],
  ).find(isValidIanaTimezone);

  if (typeof explicitTimezone === 'string') return explicitTimezone.trim();

  return isValidIanaTimezone(preferredTimezone) ? preferredTimezone.trim() : getBrowserTimeZone();
}
